import type { HistoryEntry } from '@wordle/game-engine';
import { WORDLE_VERSION } from '@/utils/getDailyWord';
const STORAGE_KEY = `wordle6:${WORDLE_VERSION}:history`;
const LEGACY_STORAGE_KEY = STORAGE_KEY;

export class LocalStorageHistoryStore {
  private namespace?: string;

  constructor(namespace?: string) {
    this.namespace = namespace;
  }

  private getStorageKey(): string {
    return this.namespace ? `${STORAGE_KEY}:${this.namespace}` : STORAGE_KEY;
  }

  private migrateLegacyHistory(): void {
    if (typeof window === 'undefined') return;
    if (!this.namespace) return;

    if (this.namespace !== 'fr') {
      const migrationKey = `${STORAGE_KEY}:migrated:${this.namespace}`;
      const legacyRaw = window.localStorage.getItem(LEGACY_STORAGE_KEY);
      const namespacedKey = this.getStorageKey();
      const namespacedRaw = window.localStorage.getItem(namespacedKey);

      if (
        legacyRaw &&
        namespacedRaw &&
        legacyRaw === namespacedRaw
      ) {
        window.localStorage.removeItem(namespacedKey);
      }

      window.localStorage.setItem(migrationKey, 'ignored');
      return;
    }

    const migrationKey = `${STORAGE_KEY}:migrated:${this.namespace}`;
    if (window.localStorage.getItem(migrationKey)) return;

    const legacyRaw = window.localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!legacyRaw) {
      window.localStorage.setItem(migrationKey, 'true');
      return;
    }

    const namespacedKey = this.getStorageKey();
    if (!window.localStorage.getItem(namespacedKey)) {
      window.localStorage.setItem(namespacedKey, legacyRaw);
    }

    window.localStorage.setItem(migrationKey, 'true');
  }

  async loadAll(): Promise<HistoryEntry[]> {
    if (typeof window === 'undefined') return [];

    this.migrateLegacyHistory();

    const raw = window.localStorage.getItem(this.getStorageKey());
    if (!raw) return [];

    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  async get(dateKey: string): Promise<HistoryEntry | null> {
    const all = await this.loadAll();
    return all.find(e => e.dateKey === dateKey) ?? null;
  }

  async save(entry: HistoryEntry): Promise<void> {
    if (typeof window === 'undefined') return;

    const all = await this.loadAll();

    const next = [
      ...all.filter(e => e.dateKey !== entry.dateKey),
      entry,
    ];

    window.localStorage.setItem(
      this.getStorageKey(),
      JSON.stringify(next)
    );
  }
}
